import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'

interface BreadcrumbTitleContextValue {
  title: string | null
  /**
   * Zu welchem Projekt der Titel gehört (`Project.id`), `null` für Seiten ohne Projekt. Die
   * Breadcrumb zeigt einen Projekt-Titel nur, solange genau dieses Projekt offen ist — beim
   * Wechsel stünde sonst einen Frame lang der alte Name hinter den neuen Stufen (#185).
   */
  titleOwner: string | null
  setTitle: (title: string | null, owner?: string | null) => void
}

const BreadcrumbTitleContext = createContext<BreadcrumbTitleContextValue>({
  title: null,
  titleOwner: null,
  setTitle: () => {},
})

export function BreadcrumbTitleProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ title: string | null; titleOwner: string | null }>({ title: null, titleOwner: null })
  const setTitle = useCallback((title: string | null, owner: string | null = null) => {
    setState(prev => {
      if (prev.title === title && prev.titleOwner === owner) return prev
      // Ein Projekt räumt nur seinen EIGENEN Titel ab. Inaktive Projekt-Tabs setzen `null` beim
      // Vorwärmen, Laden und Schließen — das löschte den Namen der aktiven Seite (Diff-Audit #273:
      // ein leeres Kurz notiert stand als „Arbeit ›“ da). Seiten ohne Projekt räumen wie bisher alles.
      if (title === null && owner !== null && prev.titleOwner !== owner) return prev
      return { title, titleOwner: owner }
    })
  }, [])
  return (
    <BreadcrumbTitleContext.Provider value={{ ...state, setTitle }}>
      {children}
    </BreadcrumbTitleContext.Provider>
  )
}

export function useBreadcrumbTitle() {
  return useContext(BreadcrumbTitleContext)
}
